// @flow
import React, { useCallback, useMemo } from 'react';
import prop from 'ramda/es/prop';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import classNames from 'classnames/bind';

import Counter from 'app/common/ui/Counter';
// $FlowFixMe
import { updateCartItem } from 'app/common/actions/shop/cart/updateCartItem';
// $FlowFixMe
import { deleteCartItem } from 'app/common/actions/shop/cart/deleteCartItem';
import { showConfirmation, hideConfirmation } from 'app/common/actions/confirmation';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';

import {
  ECartItem,
  ECartItemThumb,
  ECartItemTitle,
  ECartItemContent,
  PerItem,
} from 'app/common/layouts/ecart';

import { Price } from 'app/common/layouts/commonShop';

import createProductInCartSelector from './createProductInCartSelector';
import styles from './CartItem.module.scss';;


const cx = classNames.bind(styles);

const getConfirmProps = (name, handleDeleteProduct, handleCloseConfirmation) => ({
  text: `Удалить товар «${name}» из корзины?`,
  buttons: [{
    value: 'Удалить',
    error: true,
    onClick: handleDeleteProduct,
    key: 'remove',
  }, {
    value: 'Отмена',
    outline: true,
    onClick: handleCloseConfirmation,
    key: 'cancel',
  }],
});


type CartItemProps = {
  amount: string,
  id: string,
  image: {
    thumb: string,
    standart: string,
    high: string,
  },
  name: string,
  price: string,
  quantity: number,
  sku: string,
  slug: string,
  variantId: number,
}

type Props = {
  productId: number,
  className?: string,
  onDeleteProduct?: (void) => void,
  deleteByCross?: boolean,
  locked?: boolean,
}

const CartItem = ({
  productId,
  className,
  onDeleteProduct,
  deleteByCross,
  locked,
}: Props) => {
  const dispatch = useDispatch();

  const updating = useSelector(state => state.shop.cart.updating, shallowEqual);
  const deleting = useSelector(state => state.shop.cart.deleting, shallowEqual);

  const selectProductInCart = useMemo(createProductInCartSelector, []);

  const {
    amount,
    id,
    image,
    name,
    price,
    quantity,
  }: CartItemProps = useSelector(state => selectProductInCart(state, { id: productId }), shallowEqual); // todo свой селектор в инстансе

  const handleDeleteProduct = useCallback(async (e) => {
    if (typeof e === 'object' && e.preventDefault) {
      e.preventDefault();
    }
    dispatch(hideConfirmation());
    const res = await dispatch(deleteCartItem(id));
    if (res && typeof onDeleteProduct === 'function') {
      onDeleteProduct();
    }
  }, [dispatch, onDeleteProduct, id]);

  const handleCloseConfirmation = useCallback((e) => {
    e.preventDefault();
    dispatch(hideConfirmation());
  }, [dispatch]);

  const confirmProps = useMemo(
    () => getConfirmProps(name, handleDeleteProduct, handleCloseConfirmation),
    [name, handleDeleteProduct, handleCloseConfirmation],
  );

  const handleChangeQuantity = useCallback((num) => {
    if (num === 0) {
      dispatch(showConfirmation(confirmProps));
      return;
    }
    dispatch(updateCartItem(id, num));
  }, [dispatch, id, confirmProps]);

  return (
    <ECartItem
      id={id}
      onCrossClick={deleteByCross ? handleDeleteProduct : undefined}
      confirmationText={`Удалить «${name}» из корзины?`}
      className={cx({ locked: deleting || locked }, className)}
    >
      {<ECartItemThumb imgSrc={prop('standart', image)} title={name} />}
      <ECartItemTitle className={styles.nameCol}>
        {name}
        {/* <Stock>Есть на складе</Stock> */}
      </ECartItemTitle>
      <ECartItemContent>
        <Counter
          value={quantity}
          onChange={handleChangeQuantity}
          disabled={updating || deleting || locked}
          minValue={0}
        />
        <PerItem>{`${toFixedValueCurrency(price)}/шт.`}</PerItem>
      </ECartItemContent>
      <ECartItemContent className={styles.priceCol}>
        <Price>{toFixedValueCurrency(amount)}</Price>
      </ECartItemContent>
    </ECartItem>
  );
};

export default CartItem;
